import {FormikProps} from "formik";
import {DefaultOptionType as CascaderOptionType} from "antd/lib/cascader";
import {message} from "antd";
import {set, getYear, getMonth, getDate, getHours, getMinutes, getSeconds, add, startOfDay, endOfDay} from "date-fns";
import uuid from "uuid";
import moment, {Moment} from "moment";
import {GetLocationsSortedByCityQuery, EditCourseInput, CourseCycle, NewCourseLessonInput, EditCourseLessonInput, NewCourseInput} from "../../generated/graphql";
import {PossibleFormikPropsType} from "./CourseCreate/CourseCreateForm";
import {ISODateString, assertNever} from "../../helpers/typeScriptHelpers";
import {sortByStartDateTime} from "../../helpers/sortingFunctions";
import i18n from "../../services/i18n";

export const prepareLocationsForCascader: (locationsData: GetLocationsSortedByCityQuery["locationsSortedByCity"]) => Array<CascaderOptionType> = (locationsData) => {
    return locationsData.map((locationData) => ({
        value: locationData.city,
        label: locationData.city,
        children: locationData.locations.map((location) => ({
            value: location.id,
            label: location.name,
            children: location.venues.map((venue) => ({
                value: venue.id,
                label: venue.name,
            })),
        })),
    }));
};

export const setOnlyDate = (dateToChange: ISODateString, newDate: ISODateString): ISODateString => {
    const newDateAsDate = new Date(newDate);
    const year = getYear(newDateAsDate);
    const month = getMonth(newDateAsDate);
    const date = getDate(newDateAsDate);

    return set(new Date(dateToChange), {year, month, date}).toISOString();
};

export const setOnlyTime = (dateToChange: ISODateString, newTime: ISODateString): ISODateString => {
    const newTimeAsDate = new Date(newTime);
    const hours = getHours(newTimeAsDate);
    const minutes = getMinutes(newTimeAsDate);

    // Sekunden/Millisekunden immer nullen: Lesson-Zeiten sind minutengenau.
    // Reste aus dem Formular-Seed (new Date()) führten sonst zu Zeitstempeln
    // wie 10:00:23, die in der EndUserApp als "59min"-Dauer angezeigt wurden.
    return set(new Date(dateToChange), {hours, minutes, seconds: 0, milliseconds: 0}).toISOString();
};

export const isSameDate = (dateA: ISODateString, dateB: ISODateString): boolean => {
    const a = new Date(dateA);
    const b = new Date(dateB);
    const sameYear = getYear(a) === getYear(b);
    const sameMonth = getMonth(a) === getMonth(b);
    const sameDate = getDate(a) === getDate(b);

    return sameYear && sameMonth && sameDate;
};

export const isSameTime = (dateA: ISODateString, dateB: ISODateString): boolean => {
    const a = new Date(dateA);
    const b = new Date(dateB);
    const sameHour = getHours(a) === getHours(b);
    const sameMinute = getMinutes(a) === getMinutes(b);
    const sameSecond = getSeconds(a) === getSeconds(b);

    return sameHour && sameMinute && sameSecond;
};

/**
 * Determines the date of the last course lesson and merges it with the default start time.
 * If no lessons are present, it returns the default startDateTime.
 */
const getReferenceStartDateTime = (formikProps: FormikProps<PossibleFormikPropsType>) => {
    const {lessons, startDateTime: defaultStartDateTime} = formikProps.values;

    const lastLesson: NewCourseLessonInput | EditCourseLessonInput | undefined = lessons.slice().sort(sortByStartDateTime)[lessons.length - 1];

    const newStartDateTime = lastLesson?.startDateTime ?? undefined;

    return newStartDateTime === undefined ? new Date(defaultStartDateTime).toISOString() : setOnlyDate(defaultStartDateTime, newStartDateTime);
};

type CalculateLessonDatesFromReferenceOptions = {
    formikProps: Pick<FormikProps<PossibleFormikPropsType>, "values">;
    stepsFromReference: number;
    referenceDate: ISODateString;
    cycle?: CourseCycle;
    offset?: number;
};

/**
 * Calculates `startDateTime` and `endDateTime` for lessons, following the reference point taking into account the
 * selected `cycle`
 */
const calculateLessonDatesFromReference = ({formikProps, stepsFromReference, referenceDate, cycle: customCycle, offset = 0}: CalculateLessonDatesFromReferenceOptions) => {
    const {startDateTime: defaultStartDateTime, endDateTime: defaultEndDateTime, cycle: defaultCycle} = formikProps.values;

    const cycle = customCycle ?? defaultCycle ?? undefined;
    const reference = new Date(setOnlyTime(referenceDate, defaultStartDateTime));
    const steps = stepsFromReference + offset;

    if (cycle === undefined) {
        throw new Error('"Cycle" has not been defined in the formik props and is needed to calculate the lesson date.');
    }

    const DAYS_PER_WEEK = 7;
    const DAYS_PER_FORTNIGHT = 14;

    if (cycle === CourseCycle.Weekly) {
        const startDateTime = add(reference, {
            days: DAYS_PER_WEEK * steps,
        }).toISOString();
        const endDateTime = setOnlyTime(startDateTime, defaultEndDateTime);

        return {startDateTime, endDateTime};
    }

    if (cycle === CourseCycle.EveryTwoWeeks) {
        const startDateTime = add(reference, {
            days: DAYS_PER_FORTNIGHT * steps,
        }).toISOString();

        const endDateTime = setOnlyTime(startDateTime, defaultEndDateTime);

        return {startDateTime, endDateTime};
    }

    if (cycle === CourseCycle.Monthly) {
        const startDateTime = add(reference, {months: steps}).toISOString();
        const endDateTime = setOnlyTime(startDateTime, defaultEndDateTime);

        return {startDateTime, endDateTime};
    }

    const startDateTime = add(reference, {days: steps}).toISOString();
    const endDateTime = setOnlyTime(startDateTime, defaultEndDateTime);

    return {startDateTime, endDateTime};
};

export const appliesToAllCourseLessons = (formikProps: FormikProps<PossibleFormikPropsType>, property: "startTime" | "endTime" | "date" | "cycle") => {
    const {lessons, startDateTime: referenceDate} = formikProps.values;

    if (lessons === undefined || lessons === null || lessons.length < 1) {
        return true;
    }

    return (lessons as Array<NewCourseInput | EditCourseInput>).every((lesson, stepsFromReference) => {
        const {startDateTime: expectedStartDateTime, endDateTime: expectedEndDateTime} = calculateLessonDatesFromReference({
            formikProps,
            referenceDate,
            stepsFromReference,
        });

        switch (property) {
            case "startTime":
                return isSameTime(lesson.startDateTime, expectedStartDateTime);
            case "endTime":
                return isSameTime(lesson.endDateTime, expectedEndDateTime);
            case "date":
            case "cycle":
                return isSameDate(lesson.startDateTime, expectedStartDateTime);
            default:
                return assertNever(property);
        }
    });
};

// Ferien beim Erzeugen überspringen: höchstens so viele Zyklen probieren
export const HOLIDAY_SKIP_ATTEMPT_FACTOR = 3;

export type HolidayRange = {start: ISODateString; end: ISODateString; name?: string};

const CYCLE_DAYS: Record<CourseCycle, number> = {
    [CourseCycle.Weekly]: 7,
    [CourseCycle.EveryTwoWeeks]: 14,
    [CourseCycle.Monthly]: 31,
    [CourseCycle.Custom]: 1,
};

/**
 * Zeitraum, in dem `generateLessons` mit Ferien-Überspringen höchstens Termine sucht.
 * Wird vorab für die Ferien-Abfrage gebraucht, bevor es Termine gibt.
 */
export const getLessonGenerationRange = (values: PossibleFormikPropsType): {start: Date; end: Date} => {
    const start = startOfDay(new Date(values.startDateTime ?? Date.now()));
    const lessonCount = Math.min(values.defaultLessonCount ?? 0, 53);
    const cycle: CourseCycle = values.cycle ?? CourseCycle.Weekly;
    const days = HOLIDAY_SKIP_ATTEMPT_FACTOR * lessonCount * CYCLE_DAYS[cycle];

    return {start, end: endOfDay(add(start, {days}))};
};

// Wie die Ferien-Markierung in CourseCreateDateList über moment, also in der Zeitzone der Organisation
const findHoliday = (date: ISODateString, holidays: Array<HolidayRange>) => {
    const day = moment(date);

    return holidays.find((holiday) => day.isSameOrAfter(moment(holiday.start).startOf("day")) && day.isSameOrBefore(moment(holiday.end).endOf("day")));
};

// Kurzer Hinweis, welche Termine wegen Ferien übersprungen wurden, z. B. „12.10.2026 (Herbstferien)“
const notifySkippedLessons = (skipped: Array<{date: ISODateString; holiday: HolidayRange}>) => {
    if (skipped.length === 0) return;

    const dates = skipped.map(({date, holiday}) => `${moment(date).format("DD.MM.YYYY")}${holiday.name ? ` (${holiday.name})` : ""}`).join(", ");
    message.info(i18n.containers.courses.SkipHolidays.lessonsSkipped({dates}), 8);
};

/**
 * Termine aus Startdatum, Rhythmus und Anzahl erzeugen, Ferien optional überspringen.
 * Rein, ohne Formik-Seiteneffekte; genutzt von `generateLessons` und von „Kurs kopieren“.
 */
export const buildGeneratedLessons = (values: PossibleFormikPropsType, holidaysToSkip: Array<HolidayRange> = []) => {
    const {defaultLessonCount, startDateTime, instructors, locationId, venueId} = values;
    const limitedLessonCount = Math.min(defaultLessonCount ?? 0, 53);
    const lessons = [];
    const skipped: Array<{date: ISODateString; holiday: HolidayRange}> = [];
    const maxAttempts = holidaysToSkip.length > 0 ? HOLIDAY_SKIP_ATTEMPT_FACTOR * limitedLessonCount : limitedLessonCount;

    for (let step = 0; step < maxAttempts && lessons.length < limitedLessonCount; step++) {
        const dates = calculateLessonDatesFromReference({formikProps: {values}, referenceDate: startDateTime, stepsFromReference: step});
        const holiday = findHoliday(dates.startDateTime, holidaysToSkip);

        if (holiday) {
            skipped.push({date: dates.startDateTime, holiday});
        } else {
            lessons.push({instructors, locationId, venueId, localKey: uuid(), ...dates});
        }
    }

    return {lessons, skipped};
};

export const generateLessons = (formikProps: FormikProps<PossibleFormikPropsType>, holidaysToSkip: Array<HolidayRange> = []) => {
    const {defaultLessonCount} = formikProps.values;
    const updatedBookingsData = "updatedBookingsData" in formikProps.values ? formikProps.values.updatedBookingsData : undefined;

    // Begrenze defaultLessonCount auf maximal 53
    const limitedLessonCount = Math.min(defaultLessonCount ?? 0, 53);

    // Zeige Feedback wenn der Benutzer mehr als 53 eingegeben hat
    if ((defaultLessonCount ?? 0) > 53) {
        message.warning("Die maximale Anzahl von 53 Kurseinheiten wurde auf 53 begrenzt.");
    }

    const {lessons: newLessons, skipped} = buildGeneratedLessons(formikProps.values, holidaysToSkip);

    notifySkippedLessons(skipped);

    if (newLessons.length < limitedLessonCount) {
        message.warning(i18n.containers.courses.SkipHolidays.attemptLimitReached({created: String(newLessons.length), requested: String(limitedLessonCount)}));
    }

    // Wenn Lektionen neu generiert werden, müssen wir die updatedBookingsData so anpassen,
    // dass alle Bookings leere courseLessonIds haben. Dies signalisiert dem Backend,
    // dass es um "Neu generieren" geht (alle alten Lessons löschen + neue erstellen)
    if (updatedBookingsData && updatedBookingsData.length > 0) {
        const clearedBookingsData = updatedBookingsData.map((booking) => ({
            ...booking,
            courseLessonIds: [],
        }));
        formikProps.setFieldValue("updatedBookingsData", clearedBookingsData);
    }

    formikProps.setFieldValue("lessons", newLessons);
};

export const addLesson = (formikProps: FormikProps<PossibleFormikPropsType>, holidaysToSkip: Array<HolidayRange> = []) => {
    const {instructors, locationId, venueId} = formikProps.values;

    const lessons = formikProps.values.lessons ?? [];

    // Prüfe ob bereits 53 Lessons vorhanden sind
    if (lessons.length >= 53) {
        message.warning("Die maximale Anzahl von 53 Kurseinheiten wurde bereits erreicht. Es können keine weiteren Kurseinheiten hinzugefügt werden.");

        return;
    }

    const referenceDate = getReferenceStartDateTime(formikProps);

    // Nächsten Zyklus außerhalb der Ferien suchen, höchstens 53 Zyklen weit
    const skipped: Array<{date: ISODateString; holiday: HolidayRange}> = [];
    let dates: ReturnType<typeof calculateLessonDatesFromReference> | undefined;

    for (let step = 1; step <= 53 && !dates; step++) {
        const candidate = calculateLessonDatesFromReference({formikProps, referenceDate, stepsFromReference: step});
        const holiday = findHoliday(candidate.startDateTime, holidaysToSkip);

        if (holiday) {
            skipped.push({date: candidate.startDateTime, holiday});
        } else {
            dates = candidate;
        }
    }

    if (!dates) {
        message.warning(i18n.containers.courses.SkipHolidays.attemptLimitReached({created: "0", requested: "1"}));

        return;
    }

    notifySkippedLessons(skipped);

    const tempLessonId = uuid();
    const prefixedLessonId = `new-${tempLessonId}`;

    const updatedLessons = [
        ...lessons,
        {
            id: tempLessonId,
            // id: prefixedLessonId, // Präfix "new-" hinzufügen
            instructors,
            locationId,
            venueId,
            localKey: tempLessonId,
            // localKey: prefixedLessonId,
            ...dates,
        },
    ];

    formikProps.setFieldValue("lessons", updatedLessons);
};

export const handleDefaultDateChange = (formikProps: FormikProps<PossibleFormikPropsType>) => {
    return (value: Moment | null) => {
        const userInput = (value ?? new Date()).toISOString();
        const oldFirstLessonStartDate = formikProps.values.startDateTime ?? new Date().toISOString();
        const newFirstLessonStartDate = setOnlyDate(oldFirstLessonStartDate, userInput);
        const oldFirstLessonEndDate = formikProps.values.endDateTime ?? new Date().toISOString();
        const newFirstLessonEndDate = setOnlyDate(oldFirstLessonEndDate, userInput);

        formikProps.setValues({
            ...formikProps.values,
            startDateTime: newFirstLessonStartDate,
            endDateTime: newFirstLessonEndDate,
        });
    };
};

export const handleDefaultCourseCycleChange = (formikProps: FormikProps<PossibleFormikPropsType>) => {
    return (cycle: CourseCycle) => {
        formikProps.setFieldValue("cycle", cycle);
    };
};

export const handleDefaultTimeChange = (formikProps: FormikProps<PossibleFormikPropsType>, property: "startDateTime" | "endDateTime") => {
    return (value: Moment | null) => {
        const userInput = (value ?? new Date()).toISOString();
        const oldTime = formikProps.values[property] ?? new Date().toISOString();
        const newTime = setOnlyTime(oldTime, userInput);

        formikProps.setFieldValue(property, newTime);
    };
};

export const changeDefaultLessonCount = (formikProps: FormikProps<PossibleFormikPropsType>) => {
    return (value: number | undefined = 0) => {
        formikProps.setFieldValue("defaultLessonCount", value);
    };
};

export const lessonsAreEqual = (currentLessons?: Array<NewCourseLessonInput | EditCourseLessonInput>, initialLessons?: Array<NewCourseLessonInput | EditCourseLessonInput>): boolean => {
    if (!currentLessons || !initialLessons || currentLessons.length !== initialLessons.length) {
        return false;
    }

    return currentLessons.every((lesson, index) => {
        const initialLesson = initialLessons[index];

        return isSameDate(lesson.startDateTime, initialLesson.startDateTime) && isSameDate(lesson.endDateTime, initialLesson.endDateTime);
    });
};
